import { Injectable } from '@angular/core';
import { appendNode } from '@models/apollo';
import {
  CreateIlClassInput,
  CreateIlClassMutation,
  ForcePassIlClassResultInput,
  ForcePassIlClassResultMutation,
  LoadBookableClassesForIlCourseQuery,
  LoadIlClassBookingsQuery,
  LoadIlClassDetailsQuery,
  LoadIlClassListQuery,
  LoadIlClassListQueryVariables,
  LoadIlClassWaitlistQuery,
  UpdateIlClassInput,
  UpdateIlClassMutation,
  UploadIlClassRosterLearnerInput,
  UploadIlClassRosterLearnerMutation
} from '@queries/generated';
import { finalMap } from '@rx/final-map';
import { gidToApolloCacheKey } from '@util/gid-to-apollo-cache-key';
import { toServerDateTime, toServerDateTimes } from '@util/session-dates';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ILClassService {
  constructor(
    private loadILClassList: LoadIlClassListQuery,
    private createILClass: CreateIlClassMutation,
    private loadILClassDetails: LoadIlClassDetailsQuery,
    private loadILClassBookings: LoadIlClassBookingsQuery,
    private loadILClassWaitlist: LoadIlClassWaitlistQuery,
    private loadBookableClassesForCourse: LoadBookableClassesForIlCourseQuery,
    private updateILClass: UpdateIlClassMutation,
    private uploadIlClassRosterLearner: UploadIlClassRosterLearnerMutation,
    private forcePassIlClassResult: ForcePassIlClassResultMutation
  ) {}

  list = (input: LoadIlClassListQueryVariables) => {
    return this.loadILClassList.watch(input).valueChanges.pipe(map(({ data }) => data.ilClasses.all.nodes));
  };

  create(input: CreateIlClassInput) {
    input.ilClass = {
      ...input.ilClass,
      bookingEndsAt: toServerDateTime(input.ilClass.bookingEndsAt),
      bookingStartsAt: toServerDateTime(input.ilClass.bookingStartsAt),
      ilSessions: input.ilClass.ilSessions.map((ilSession) => {
        return {
          ...ilSession,
          ...toServerDateTimes(ilSession)
        };
      })
    };
    return this.createILClass.mutate({ input }, { refetchQueries: ['LoadILClassList'] }).pipe(
      map(({ data }) => data.createIlClass),
      finalMap('ilClass')
    );
  }

  find = (id: number) => {
    return this.loadILClassDetails.watch({ id }).valueChanges.pipe(map(({ data }) => data.ilClasses.findById));
  };

  loadBookings = (id: number) => {
    const variables = { id, first: 250 };
    const query = this.loadILClassBookings.watch(variables);

    return query.valueChanges.pipe(
      map(({ data }) => data.ilClasses.findById.ilBookings),
      tap(({ pageInfo }) => {
        if (pageInfo.hasNextPage) {
          query.fetchMore({ variables: { ...variables, after: pageInfo.endCursor } });
        }
      }),
      map((response) => response.nodes)
    );
  };

  loadWaitlist = (id: number) => {
    const variables = { id, first: 250 };
    const query = this.loadILClassWaitlist.watch(variables);

    return query.valueChanges.pipe(
      map(({ data }) => data.ilClasses.findById.waitlist),
      tap(({ pageInfo }) => {
        if (pageInfo.hasNextPage) {
          query.fetchMore({ variables: { ...variables, after: pageInfo.endCursor } });
        }
      }),
      map((response) => response.nodes)
    );
  };

  loadBookableClassesForILCourse = (ilCourseId: number, reservable = false) => {
    return this.loadBookableClassesForCourse
      .watch({ ilCourseId, reservable })
      .valueChanges.pipe(map(({ data }) => data.ilClasses.bookable.nodes));
  };

  update(input: UpdateIlClassInput) {
    const patch = { ...input.patch };

    if (patch.bookingStartsAt) {
      patch.bookingStartsAt = toServerDateTime(patch.bookingStartsAt);
    }

    if (patch.bookingEndsAt) {
      patch.bookingEndsAt = toServerDateTime(patch.bookingEndsAt);
    }

    // only patch sessions if we have them, cancel and status changes don't pass all attrs
    if (patch.ilSessions) {
      patch.ilSessions = patch.ilSessions.map((ilSession) => {
        return {
          ...ilSession,
          ...toServerDateTimes(ilSession)
        };
      });
    }

    return this.updateILClass.mutate({ input: { ...input, patch } }, { refetchQueries: ['LoadILClassDetails'] }).pipe(
      map(({ data }) => data.updateIlClass),
      finalMap('ilClass')
    );
  }

  forcePassClassResult(input: ForcePassIlClassResultInput) {
    return this.forcePassIlClassResult.mutate({ input }).pipe(
      map(({ data }) => data.forcePassIlClassResult),
      finalMap('ilClassResult')
    );
  }

  uploadRosterLearner(input: UploadIlClassRosterLearnerInput) {
    return this.uploadIlClassRosterLearner
      .mutate(
        { input },
        {
          update: (store, { data: { uploadIlClassRosterLearner } }) => {
            const id = gidToApolloCacheKey(input.ilClassId);

            // Make sure to add the new booking to the cached list of bookings for an IL Class
            store.modify({
              id,
              fields: {
                ilBookings: appendNode(uploadIlClassRosterLearner.ilBooking)
              }
            });
          }
        }
      )
      .pipe(
        map(({ data }) => data.uploadIlClassRosterLearner),
        finalMap('ilBooking')
      );
  }
}
